import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS } from 'loyalty/constants/loyaltyBonusPoints'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getLuxPlusSubscriptionRenewalOrExpiryDate } from 'selectors/luxPlusSelectors'
import LuxPlusManageAccountModalBenefitsBlob from './LuxPlusManageAccountModalBenefitsBlob'
import LuxPlusManageAccountModalLoyaltyBenefitsBlob from './LuxPlusManageAccountModalLoyaltyBenefitsBlob'
import LuxPlusManageAccountModalMembershipStatusBanner from './LuxPlusManageAccountModalMembershipStatusBanner'

interface Props {
  item: App.MemberSubscriptionItem
  onBack: () => void
}

function LuxPlusManageAccountModalRenewalReactivationSuccess({
  item,
  onBack,
}: Props) {
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])
  const subscriptionRenewalOrExpiryDate = useAppSelector(getLuxPlusSubscriptionRenewalOrExpiryDate)
  const intl = useIntl()

  const manageMembershipTitle = intl.formatMessage({
    id: 'ozD5ues9OZjW8HJ5lHcd',
    defaultMessage: 'Manage membership',
    description: 'Heading title for LuxPlus+ manage membership modal header',
  })

  const bonusPointsLabel = intl.formatMessage({
    id: 'heQTKiltDaocJwPeAXl8',
    defaultMessage: 'Bonus points',
    description: 'Label for loyalty tier bonus points pill in renewal success modal',
  })

  const allDoneLabel = intl.formatMessage({
    id: 'V6qCDk20BAuJ2OYvm5W7',
    defaultMessage: 'All done',
    description: 'Primary button text to dismiss the renewal reactivation success modal',
  })

  const moreOnLoyaltyLabel = intl.formatMessage(
    {
      id: 't6DmD_LF9qTdVfh7K8EW',
      defaultMessage: 'More on {loyaltyProgramName}',
      description: 'Secondary button text linking to loyalty program page from renewal success modal',
    },
    { loyaltyProgramName: getLoyaltyProgramDisplayName() },
  )

  return <>
    <ModalHeader title={manageMembershipTitle} isOverlay={isLoyaltyLuxPlusRenewalBonusEnabled} />
    {!isLoyaltyLuxPlusRenewalBonusEnabled && <ModalHeader title={manageMembershipTitle} onBackButtonClick={onBack} />}
    <ModalBody>
      {isLoyaltyLuxPlusRenewalBonusEnabled && <ProductPaletteProvider palette={`tier-${tier}`}>
        <Sheet padding={24} background={`tier-${tier}-secondary`}>
          <Group direction="vertical" gap={16}>
            <Group direction="vertical" gap={12}>
              <LoyaltyTierPill tier={tier} kind="primary">{bonusPointsLabel}</LoyaltyTierPill>
              {!!subscriptionRenewalOrExpiryDate && <Heading variant="heading4">
                <FormattedMessage
                  id="uayYa6Ey89Kn4F3jRqHa"
                  defaultMessage="Success! Your {programName} membership renews on {date}, earning you"
                  description="Success heading showing membership renewal date in renewal reactivation success modal"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    date: <FormatDateTime value={subscriptionRenewalOrExpiryDate} dateStyle="medium" />,
                  }}
                />
              </Heading>}
              <Heading variant="heading3" colour={tier === 'platinum' ? `tier-${tier}-dark-contrast` : `tier-${tier}-light-contrast`}>
                <i><FormattedMessage
                  id="Ry2EsHoK8K6hD4OTxpb5"
                  defaultMessage="{statusCredits} and {points}"
                  description="Loyalty bonus showing status credits and points earned from LuxPlus+ membership renewal"
                  values={{
                    statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
                    points: <FormatLoyaltyPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
                  }}
                />
                </i>
              </Heading>
              <LuxPlusManageAccountModalLoyaltyBenefitsBlob onLinkClick={dismissModal} />
              <BodyText variant="small">
                <FormattedMessage
                  id="N9wyLhvi-lVXvYIbXrnw"
                  defaultMessage="Offer only available on renewal of {programName} membership. <link>Terms & conditions apply</link>"
                  description="Disclaimer text with terms and conditions link for LuxPlus+ renewal bonus offer in success modal"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    link: (chunks) => <TextLink to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} onClick={dismissModal}>{chunks}</TextLink>,
                  }}
                />
              </BodyText>
            </Group>
          </Group>
        </Sheet>
      </ProductPaletteProvider>}
      <ModalContent>
        <Group direction="vertical" gap={24}>
          <Group direction="vertical" gap={4}>
            {isLoyaltyLuxPlusRenewalBonusEnabled && <BodyText variant="medium" colour="lux-plus">
              <FormattedMessage
                id="4lyLqZ87VsbXTUAC5CCz"
                defaultMessage="Plus unlimited <b>{programName}</b> member only benefits:"
                description="Subheading for member benefits list when loyalty renewal bonus is enabled in renewal success modal"
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>}
            {!isLoyaltyLuxPlusRenewalBonusEnabled && <BodyText variant="medium" colour="lux-plus">
              <FormattedMessage
                id="Lc5DyiZouF-GqSuEZ0SD"
                defaultMessage="Unlimited <b>{programName}</b> member only benefits:"
                description="Subheading for member benefits list when loyalty renewal bonus is not enabled in renewal success modal"
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>}
            <LuxPlusManageAccountModalBenefitsBlob onLinkClick={dismissModal} />
          </Group>
          <LuxPlusManageAccountModalMembershipStatusBanner item={item} />
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{ children: allDoneLabel, onClick: dismissModal }}
      secondaryActionProps={{ children: moreOnLoyaltyLabel, to: `/${LOYALTY_LANDING_PAGE}` }}
    />
  </>
}

export default LuxPlusManageAccountModalRenewalReactivationSuccess
